import { useMemo } from 'react';
import { useApiFiltersContext } from 'contexts/ApiFiltersContext/ApiFiltersContext';
import {
    Suggestions,
    SuggestionsType,
    isContentSuggestionPayload,
    isModifiersPayload,
} from '../types';

const usePredictiveInput = ({
    inputValue,
    selectedSuggestionIndex,
    suggestions,
}: {
    inputValue: string;
    selectedSuggestionIndex: number;
    suggestions: Suggestions[];
}) => {
    const { getFilterLabel } = useApiFiltersContext();

    const [predictiveModifiers, predictiveInput] = useMemo(() => {
        let predictiveInput = '';

        const firstSuggestion = suggestions?.[0];
        const suggestionType = firstSuggestion?.[1];

        const hasUnsupportedSuggestionType = [
            SuggestionsType.Label,
            SuggestionsType.Pack,
        ].includes(suggestionType);

        const disablePredictiveInput =
            hasUnsupportedSuggestionType || !inputValue;

        const fallbackSuggestionIndex = disablePredictiveInput ? -1 : 0;

        const hasSelectedSuggestionIndex = selectedSuggestionIndex !== -1;
        const suggestionsIndex = hasSelectedSuggestionIndex
            ? selectedSuggestionIndex
            : fallbackSuggestionIndex;

        const showInlineSuggestion = suggestionsIndex !== -1;

        if (
            !suggestions ||
            !showInlineSuggestion ||
            suggestions?.length - 1 < suggestionsIndex
        ) {
            return [undefined, predictiveInput];
        }

        const [modifiers] = suggestions[suggestionsIndex] ?? [];

        if (isContentSuggestionPayload(modifiers)) {
            const [, { name }] = modifiers;

            predictiveInput = name ? name : '';
        } else if (isModifiersPayload(modifiers)) {
            const [params, slug] = modifiers;

            predictiveInput = slug ? getFilterLabel(slug, params) : '';
        }

        return [modifiers, predictiveInput];
    }, [inputValue, selectedSuggestionIndex, suggestions, getFilterLabel]);

    return { predictiveModifiers, predictiveInput };
};

export default usePredictiveInput;
